import { getSdkAuthStore } from '../auth/state/auth-state.js';
import { privateAccountScope } from '../core/internal/private-account-entity.js';

const PRIVATE_PROFILE_FIELDS = [
  'email',
  'phoneNumber',
  'interestTopics',
  'interestPlatforms',
  'onboardingCompleted',
] as const;

export function applyPrivateAccountProfileOptimistic<T>(
  previous: T,
  variables: Record<string, unknown>,
  queryKey: readonly unknown[],
  _queryClient: unknown,
  viewerScope: string,
  networkScope: string,
): T {
  return updatePrivateAccount(previous, queryKey, viewerScope, networkScope, (account) => {
    let next = account;
    for (const field of PRIVATE_PROFILE_FIELDS) {
      const value = variables[field];
      if (value !== undefined && next[field] !== value) next = { ...next, [field]: value };
    }
    if (variables.email !== undefined && next.emailVerified !== false) {
      next = { ...next, emailVerified: false };
    }
    return next;
  });
}

export function applyPrivateAccountLocationOptimistic<T>(
  previous: T,
  _variables: Record<string, unknown>,
  queryKey: readonly unknown[],
  _queryClient: unknown,
  viewerScope: string,
  networkScope: string,
): T {
  return updatePrivateAccount(previous, queryKey, viewerScope, networkScope, (account) =>
    account.locationSource === 'manual' ? account : { ...account, locationSource: 'manual' },
  );
}

export function applyPrivateAccountGiftsDisabledOptimistic<T>(
  previous: T,
  variables: Record<string, unknown>,
  queryKey: readonly unknown[],
  _queryClient: unknown,
  viewerScope: string,
  networkScope: string,
): T {
  return updatePrivateAccount(previous, queryKey, viewerScope, networkScope, (account) =>
    account.giftsDisabled === variables.disabled
      ? account
      : { ...account, giftsDisabled: variables.disabled },
  );
}

export function applyPrivateAccountGiftIndexingOptimistic<T>(
  previous: T,
  variables: Record<string, unknown>,
  queryKey: readonly unknown[],
  _queryClient: unknown,
  viewerScope: string,
  networkScope: string,
): T {
  return updatePrivateAccount(previous, queryKey, viewerScope, networkScope, (account) =>
    account.giftIndexingEnabled === variables.enabled
      ? account
      : { ...account, giftIndexingEnabled: variables.enabled },
  );
}

export function applyPrivateAccountSessionRevokeOptimistic<T>(
  previous: T,
  variables: Record<string, unknown>,
  queryKey: readonly unknown[],
  _queryClient: unknown,
  viewerScope: string,
  networkScope: string,
): T {
  return updatePrivateAccount(previous, queryKey, viewerScope, networkScope, (account) => {
    if (!Array.isArray(account.sessions) || typeof variables.id !== 'string') return account;
    const sessions = account.sessions.filter(
      (session) =>
        session === null ||
        typeof session !== 'object' ||
        (session as Record<string, unknown>).id !== variables.id,
    );
    return sessions.length === account.sessions.length ? account : { ...account, sessions };
  });
}

function updatePrivateAccount<T>(
  previous: T,
  queryKey: readonly unknown[],
  viewerScope: string,
  networkScope: string,
  update: (account: Record<string, unknown>) => Record<string, unknown>,
): T {
  const userId = getSdkAuthStore().current().user?.id;
  if (
    userId === undefined ||
    queryKey[0] !== 'accounts' ||
    queryKey[1] !== 'viewer' ||
    queryKey[2] !== privateAccountScope(viewerScope, networkScope, userId) ||
    queryKey[4] !== userId
  ) {
    return previous;
  }
  if (previous === null || typeof previous !== 'object') return previous;
  return update(previous as Record<string, unknown>) as T;
}
